import LeadCaptureBox from "@/components/LeadCaptureBox";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { ArrowRight, ChevronRight } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";

const TOOLS = [
  {
    to: "/spam-checker",
    emoji: "🛡",
    label: "Spam Score Checker",
    desc: "Paste your subject and body. Get an AI spam score, trigger word flags, and fixes in seconds.",
    badge: "Most popular",
  },
  {
    to: "/subject-line-tester",
    emoji: "📈",
    label: "Subject Line Tester",
    desc: "Score any subject line for open rate potential, spam words, and get 3 A/B variants instantly.",
    badge: null,
  },
  {
    to: "/warmup-calculator",
    emoji: "🔥",
    label: "Warmup Calculator",
    desc: "Generate a custom week-by-week email warmup schedule based on your domain and sending goals.",
    badge: null,
  },
  {
    to: "/cold-email-generator",
    emoji: "✉",
    label: "Cold Email Generator",
    desc: "AI writes a personalized cold email and 3-day follow-up from your prospect details in seconds.",
    badge: null,
  },
  {
    to: "/blacklist-checker",
    emoji: "🔍",
    label: "Blacklist Checker",
    desc: "Check your domain or IP against 20+ major email blacklists. Know if you're listed before you send.",
    badge: null,
  },
  {
    to: "/deliverability-grader",
    emoji: "🎓",
    label: "Deliverability Grader",
    desc: "Get an A–F letter grade for your sending domain across 16 checks: SPF, DKIM, DMARC, and more.",
    badge: null,
  },
  {
    to: "/roi-calculator",
    emoji: "💰",
    label: "Email ROI Calculator",
    desc: "See how much revenue your email list can generate. Instant projections with industry benchmarks.",
    badge: "New",
  },
  {
    to: "/email-signature-generator",
    emoji: "✍️",
    label: "Email Signature Generator",
    desc: "Create a professional HTML email signature for Gmail, Outlook, and Apple Mail. 4 styles, custom colors.",
    badge: "New",
  },
  {
    to: "/dmarc-checker",
    emoji: "🔐",
    label: "DMARC / SPF / DKIM Checker",
    desc: "Check any domain's email authentication records. Get an instant grade with plain-English fix recommendations.",
    badge: "New",
  },
  {
    to: "/email-copy-analyzer",
    emoji: "📝",
    label: "Email Copy Analyzer",
    desc: "Paste your email and get instant scoring on readability, spam risk, CTA strength, and personalization.",
    badge: "New",
  },
  {
    to: "/list-growth-calculator",
    emoji: "📈",
    label: "List Growth Calculator",
    desc: "Project your email list size over 12 months. See the impact of reducing churn and growing signups.",
    badge: "New",
  },
  {
    to: "/newsletter-benchmark",
    emoji: "🏆",
    label: "Newsletter Benchmark Report",
    desc: "Compare your open rate, click rate, and bounce rate to industry averages. Get a personalized grade.",
    badge: "New",
  },
];

export default function ToolsIndexPage() {
  return (
    <>
      <SeoHead
        title="Free Email Tools — MoreMail | Spam Checker, Warmup Calculator & More"
        description="6 free email deliverability tools. Check spam scores, test subject lines, calculate warmup schedules, generate cold emails, and grade your domain — no signup required."
        url={`${SITE_URL}/tools`}
        breadcrumbs={[
          { name: "Home", url: SITE_URL },
          { name: "Free Tools", url: `${SITE_URL}/tools` },
        ]}
      />

      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate">
          <Link to="/" className="hover:text-ink transition-colors">Home</Link>
          <ChevronRight size={12} />
          <span className="text-ink">Free Tools</span>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-8 sm:pb-10 text-center">
        <div className="inline-flex items-center px-3 py-1.5 bg-coral/10 border border-coral/20 rounded-full text-coral text-xs font-bold mb-4">
          6 free tools — no signup required
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-ink leading-tight mb-3">
          Free email deliverability tools
        </h1>
        <p className="text-slate text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
          Everything you need to test, improve, and protect your email deliverability — before you send a single email.
        </p>
      </section>

      {/* Tools grid */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TOOLS.map((tool) => (
            <Link
              key={tool.to}
              to={tool.to}
              className="group relative bg-white rounded-2xl border border-black/[0.08] p-5 hover:border-coral/30 hover:shadow-md transition-all"
            >
              {tool.badge && (
                <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-coral/10 text-coral border border-coral/20">
                  {tool.badge}
                </span>
              )}
              <div className="text-2xl mb-3" aria-hidden="true">{tool.emoji}</div>
              <h2 className="font-display text-base font-bold text-ink mb-1.5 group-hover:text-coral transition-colors pr-16">
                {tool.label}
              </h2>
              <p className="text-xs text-slate leading-relaxed mb-4">{tool.desc}</p>
              <div className="inline-flex items-center gap-1 text-xs font-semibold text-coral">
                Try it free <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 bg-ink rounded-2xl px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-5 text-center sm:text-left">
          <div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-1">
              Want all of this built in?
            </h2>
            <p className="text-sm text-white/50 max-w-sm">
              MoreMail runs these checks automatically on every campaign — plus warmup, AI writing, and analytics.
            </p>
          </div>
          <LeadCaptureBox />
        </div>
      </section>
    </>
  );
}
